import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { PublicLayout } from '@/layouts/PublicLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { HomePage } from '@/pages/HomePage';
import { AboutPage } from '@/pages/AboutPage';
import { FeaturesPage } from '@/pages/FeaturesPage';
import { BenefitsPage } from '@/pages/BenefitsPage';
import { ComparePage } from '@/pages/ComparePage';
import { FAQPage } from '@/pages/FAQPage';
import { PricingPage } from '@/pages/PricingPage';
import { SupportPage } from '@/pages/SupportPage';
import { StatusPage } from '@/pages/StatusPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { LeadsPage } from '@/pages/dashboard/LeadsPage';
import { CSVUploadPage } from '@/pages/dashboard/CSVUploadPage';
import { DoNotContactPage } from '@/pages/dashboard/DoNotContactPage';
import { SettingsPage } from '@/pages/dashboard/SettingsPage';
import { ProfilePage } from '@/pages/dashboard/ProfilePage';
import { SubscriptionPage } from '@/pages/dashboard/SubscriptionPage';
import { MorningBriefPage } from '@/pages/dashboard/MorningBriefPage';
import { MatchingHubPage } from '@/pages/dashboard/MatchingHubPage';
import { WarmIntrosPage } from '@/pages/dashboard/WarmIntrosPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { TermsPage } from '@/pages/legal/TermsPage';
import { PrivacyPage } from '@/pages/legal/PrivacyPage';
import { SMSCompliancePage } from '@/pages/legal/SMSCompliancePage';
import { EmailCompliancePage } from '@/pages/legal/EmailCompliancePage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { ExtensionAuthPage } from '@/pages/ExtensionAuthPage';
import { ExtensionInstallPage } from '@/pages/ExtensionInstallPage';

function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-cacao-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.ABOUT} element={<AboutPage />} />
        <Route path={ROUTES.FEATURES} element={<FeaturesPage />} />
        <Route path={ROUTES.BENEFITS} element={<BenefitsPage />} />
        <Route path={ROUTES.COMPARE} element={<ComparePage />} />
        <Route path={ROUTES.FAQ} element={<FAQPage />} />
        <Route path={ROUTES.PRICING} element={<PricingPage />} />
        <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
        <Route path={ROUTES.SMS_COMPLIANCE} element={<SMSCompliancePage />} />
        <Route path={ROUTES.EMAIL_COMPLIANCE} element={<EmailCompliancePage />} />
      </Route>

      <Route path={ROUTES.STATUS} element={<StatusPage />} />
      <Route path={ROUTES.EXTENSION_AUTH} element={<ExtensionAuthPage />} />
      <Route path={ROUTES.EXTENSION_INSTALL} element={<ExtensionInstallPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />
        <Route element={<DashboardLayout />}>
          <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
          <Route path={ROUTES.MORNING_BRIEF} element={<MorningBriefPage />} />
          <Route path={ROUTES.LEADS} element={<LeadsPage />} />
          <Route path={ROUTES.MATCHING} element={<MatchingHubPage />} />
          <Route path={ROUTES.WARM_INTROS} element={<WarmIntrosPage />} />
          <Route path={ROUTES.LEADS_IMPORT} element={<CSVUploadPage />} />
          <Route path={ROUTES.DO_NOT_CONTACT} element={<DoNotContactPage />} />
          <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
          <Route path="/dashboard/profile" element={<ProfilePage />} />
          <Route path="/dashboard/subscription" element={<SubscriptionPage />} />
        </Route>
      </Route>

      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path={`${ROUTES.ADMIN}/*`} element={<AdminDashboardPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
}

export default App;
